import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import ImageUploader from '../../components/ImageUploader';

export const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    beforeImage: '',
    afterImage: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await api.get('/admin/gallery');
      setItems(response.data.items);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      title: '',
      category: 'Coiffure',
      beforeImage: '',
      afterImage: '',
      description: '',
      isActive: true
    });
    setEditDialog({ open: true, item: null });
  };

  const openEditDialog = (item) => {
    setFormData({
      title: item.title,
      category: item.category,
      beforeImage: item.beforeImage || '',
      afterImage: item.afterImage,
      description: item.description || '',
      isActive: item.isActive !== false
    });
    setEditDialog({ open: true, item });
  };

  const handleSave = async () => {
    try {
      if (editDialog.item) {
        await api.patch(`/admin/gallery/${editDialog.item.id}`, formData);
        toast.success('Image mise à jour');
      } else {
        await api.post('/admin/gallery', formData);
        toast.success('Image ajoutée');
      }
      setEditDialog({ open: false, item: null });
      fetchGallery();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await api.delete(`/admin/gallery/${itemId}`);
      toast.success('Image supprimée');
      setDeleteDialog({ open: false, item: null });
      fetchGallery();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion de la Galerie</h1>
            <p className="text-sm md:text-base text-[#5D4037] mt-1">Gérez vos photos avant/après</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
            <Plus className="mr-2" size={20} />
            Ajouter une image
          </Button>
        </div>
      </div>

      {/* Gallery Grid - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {items.map((item) => (
          <Card key={item.id} className={!item.isActive ? 'opacity-60' : ''}>
            <CardContent className="p-0">
              <div className="relative">
                {item.beforeImage ? (
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img src={item.beforeImage} alt="Avant" className="w-full h-32 md:h-40 object-cover" />
                      <span className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">Avant</span>
                    </div>
                    <div className="relative">
                      <img src={item.afterImage} alt="Après" className="w-full h-32 md:h-40 object-cover" />
                      <span className="absolute bottom-2 left-2 bg-[#C9A96E] text-white text-xs px-2 py-1 rounded">Après</span>
                    </div>
                  </div>
                ) : (
                  <img src={item.afterImage} alt={item.title} className="w-full h-32 md:h-40 object-cover" />
                )}
              </div>
              <div className="p-3 md:p-4">
                <p className="text-xs text-[#A67B5B] uppercase">{item.category}</p>
                <h3 className="font-semibold text-[#2C1810] text-sm md:text-base">{item.title}</h3>
                {item.description && (
                  <p className="text-xs md:text-sm text-[#5D4037] line-clamp-2 mt-1">{item.description}</p>
                )}
                <div className="flex gap-2 mt-3 md:mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(item)}
                    className="flex-1 text-xs md:text-sm"
                  >
                    <Edit2 size={14} className="mr-1" /> Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setDeleteDialog({ open: true, item })}
                    className="text-red-600 hover:text-red-700 shrink-0"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12">
          <Image size={48} className="mx-auto text-[#D4C4B5] mb-4" />
          <p className="text-[#5D4037]">Aucune image dans la galerie</p>
        </div>
      )}

      {/* Edit/Create Dialog - Mobile Optimized */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="w-[95vw] sm:w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editDialog.item ? 'Modifier l\'image' : 'Nouvelle image'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Transformation Box Braids"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ex: Coiffure, Manucure..."
              />
            </div>
            
            <ImageUploader
              category="gallery"
              currentImage={formData.beforeImage}
              onImageUploaded={(url) => setFormData({ ...formData, beforeImage: url })}
              label="Image Avant (optionnel)"
            />
            
            <ImageUploader
              category="gallery"
              currentImage={formData.afterImage}
              onImageUploaded={(url) => setFormData({ ...formData, afterImage: url })}
              label="Image Après *"
            />
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la transformation..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label>Visible sur le site</Label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, item: null })} className="w-full sm:w-auto">
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
                {editDialog.item ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette image ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.item?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
